/**
 * PoiVitrinePubliquePage — vitrine touriste PUBLIQUE (sans compte).
 *
 * Accessible via /commune/:slug/points-interet, hors authentification.
 * Un visiteur voit la carte + les points affichés d'une commune + infos pratiques,
 * sans créer de compte. Lecture seule (api.public.poi).
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Badge, Alert } from '@/components/ui';
import { PoiMap } from '@/components/poi/PoiMap';
import {
  POI_FAMILLE_LABEL, POI_FAMILLE_COLOR, POI_TYPE_LABEL, POI_TYPE_EMOJI,
  type PoiFamille,
} from '@/components/poi/poiTypes';
import { api, ApiException } from '@/lib/api';
import type { PoiPoint } from '@/lib/api';
import { Clock, MapPin, Phone, ExternalLink } from 'lucide-react';

const FAMILLES: PoiFamille[] = ['securite', 'entraide', 'tourisme'];

export function PoiVitrinePubliquePage() {
  const { slug } = useParams<{ slug: string }>();
  const [communeNom, setCommuneNom] = useState('');
  const [points, setPoints] = useState<PoiPoint[]>([]);
  const [filtre, setFiltre] = useState<PoiFamille | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await api.public.poi(slug);
      setCommuneNom(r.commune.nom);
      setPoints(r.points);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Commune introuvable ou non publique.');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { void reload(); }, [reload]);

  const filtres = filtre === 'all' ? points : points.filter((p) => p.famille === filtre);

  function itineraire(p: PoiPoint): string | null {
    if (p.latitude == null || p.longitude == null) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${p.latitude},${p.longitude}`;
  }

  return (
    <div className="min-h-screen bg-surface text-ink">
      {/* En-tête public simple (pas d'AppHeader : visiteur non connecté) */}
      <header className="border-b border-border bg-surface-hi px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-grc font-bold text-white">V</span>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-ink-muted">
              {communeNom ? `Commune de ${communeNom}` : 'Vision Pro Collectivité'}
            </div>
            <div className="text-sm font-semibold">Points d'intérêt</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <h1 className="text-2xl font-semibold">Découvrir la commune</h1>
        <p className="mt-1 text-sm text-ink-subtle">
          Défibrillateurs, points d'eau, boîtes à livres, monuments, points de vue… tout sur la carte.
          Aucune inscription nécessaire.
        </p>

        {err && <Alert variant="error" className="mt-4">{err}</Alert>}

        {/* Invitation à découvrir GeoTag (application gratuite). */}
        <a
          href={import.meta.env.VITE_GEOTAG_URL || 'https://geotag.oneforall-distribution.fr'}
          target="_blank" rel="noreferrer"
          className="mt-4 flex items-center gap-3 rounded-xl border border-grc/30 bg-grc-tint/40 p-3 transition-colors hover:bg-grc-tint/60"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-grc font-bold text-white">G</span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-ink">Explorer les alentours avec GeoTag</span>
            <span className="block text-[11px] text-ink-subtle">
              Application gratuite pour découvrir les lieux et bons plans de toute la région. Inscription libre.
            </span>
          </span>
          <ExternalLink size={16} className="shrink-0 text-grc-fg" />
        </a>

        <div className="mb-4 mt-4 flex flex-wrap gap-2">
          <FamChip active={filtre === 'all'} onClick={() => setFiltre('all')} label="Tout" />
          {FAMILLES.map((fam) => (
            <FamChip key={fam} active={filtre === fam} onClick={() => setFiltre(fam)}
              label={POI_FAMILLE_LABEL[fam]} color={POI_FAMILLE_COLOR[fam]} />
          ))}
          <span className="ml-auto self-center text-xs text-ink-muted">{filtres.length} point·s</span>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
          <Card className="overflow-hidden p-0">
            <PoiMap points={filtres} height={480} />
          </Card>

          <div className="flex max-h-[480px] flex-col gap-2.5 overflow-y-auto pr-1">
            {loading ? (
              <p className="text-sm text-ink-subtle">Chargement…</p>
            ) : filtres.length === 0 ? (
              <Card className="p-4 text-sm text-ink-subtle">Aucun point à afficher.</Card>
            ) : filtres.map((p) => (
              <Card key={p.id} className="p-3">
                <div className="flex items-start gap-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-lg"
                    style={{ background: POI_FAMILLE_COLOR[p.famille] + '22' }}>
                    {POI_TYPE_EMOJI[p.type] ?? '📍'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{p.nom}</div>
                    <div className="text-[11px] text-ink-muted">{POI_TYPE_LABEL[p.type] ?? p.type}</div>
                  </div>
                  <Badge variant="neutral" size="sm">{POI_FAMILLE_LABEL[p.famille]}</Badge>
                </div>
                {p.photoUrl && <img src={p.photoUrl} alt={p.nom} className="mt-2 max-h-40 w-full rounded-lg object-cover" />}
                {p.description && <p className="mt-2 text-xs text-ink-subtle">{p.description}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-subtle">
                  {p.adresse && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.adresse}</span>}
                  {p.horaires && <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.horaires}</span>}
                  {p.telephone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {p.telephone}</span>}
                </div>
                <div className="mt-2 flex gap-3 text-xs">
                  {itineraire(p) && (
                    <a href={itineraire(p)!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-grc-fg hover:underline">
                      Itinéraire <ExternalLink size={11} />
                    </a>
                  )}
                  {p.siteWeb && (
                    <a href={p.siteWeb} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-grc-fg hover:underline">
                      Site web <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-ink-muted">
          Propulsé par Vision Pro Collectivité — One For All Distribution
        </p>
      </main>
    </div>
  );
}

function FamChip({ active, onClick, label, color }: { active: boolean; onClick: () => void; label: string; color?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${active ? 'border-transparent text-ink' : 'border-border text-ink-subtle'}`}
      style={active && color ? { background: color + '22' } : active ? { background: 'var(--surface-2)' } : {}}>
      {color && <span className="h-2 w-2 rounded-full" style={{ background: color }} />}
      {label}
    </button>
  );
}
